class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      if (!this.pendingSubmission) return;
      this.pendingSubmission = false;
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', (event) => {
      // Local list searches and a moving range thumb must never submit a facet.
      if (event.target.closest('[data-cn-local-filter]') || event.target.matches('[data-cn-price-handle]')) return;
      this.pendingSubmission = true;
      this.debouncedOnSubmit(event);
    });

    if (facetForm.id === 'FacetFiltersFormMobile') {
      const drawer = this.closest('menu-drawer');
      const summary = drawer?.querySelector('summary');
      const closeDrawer = event => {
        summary?.setAttribute('aria-expanded', 'false');
        drawer?.closeMenuDrawer?.(event, summary);
      };
      const apply = event => {
        event.preventDefault();
        this.pendingSubmission = false;
        const params = this.createSearchParams(facetForm);
        if (params === null) { facetForm.querySelector('[aria-invalid="true"]')?.focus(); return; }
        if (params !== FacetFiltersForm.searchParamsPrev) this.onSubmitForm(params, null);
        closeDrawer(event);
      };
      // Flush the current selection immediately: a quick tap on Apply must not
      // race the delayed input handler or the generic MenuDrawer close listener.
      facetForm.addEventListener('click', event => {
        if (event.target.closest('[data-cn-filter-apply]')) {
          event.stopImmediatePropagation();
          apply(event);
        } else if (event.target === facetForm) closeDrawer(event);
      }, true);
      facetForm.addEventListener('submit', apply);
      summary?.addEventListener('click', () => {
        setTimeout(() => {
          if (!drawer?.querySelector('details')?.classList.contains('menu-opening')) return;
          const panel = facetForm.querySelector('[role="dialog"]');
          if (panel && typeof trapFocus === 'function') trapFocus(panel, panel.querySelector('.mobile-facets__close'));
        });
      });
    }

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static normalizeSearchParams(searchParams) {
    const params = new URLSearchParams(searchParams), current = new URLSearchParams(location.search);
    ['q', 'view', 'preview_theme_id', 'preview_key', 'key', '_fd', '_sc', '_ab', 'country', 'currency', 'locale'].forEach(name => {
      if (!params.has(name)) current.getAll(name).forEach(value => params.append(name, value));
    });
    if (document.querySelector('.cn-search-page')) params.set('options[unavailable_products]', 'last');
    return params.toString();
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    searchParams = FacetFiltersForm.normalizeSearchParams(searchParams);
    const requestSequence = ++FacetFiltersForm.requestSequence;
    document.querySelector('[data-cn-filter-error]')?.remove();
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event, requestSequence);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event, requestSequence = FacetFiltersForm.requestSequence) {
    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error('Facet response unavailable');
        return response.text();
      })
      .then((responseText) => {
        if (requestSequence !== FacetFiltersForm.requestSequence) return;
        const html = responseText;
        // A successful HTTP status can still contain an error/redirect page.
        // Validate before mutating controls; a broken response must stay retryable.
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const expectedIds = ['ProductGridContainer', 'ProductCount'];
        if (document.getElementById('FacetFiltersFormMobile')) expectedIds.push('FacetFiltersFormMobile');
        if (expectedIds.some(id => !parsed.getElementById(id))) throw new Error('Facet section incomplete');
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      })
      .catch(() => {
        if (requestSequence !== FacetFiltersForm.requestSequence) return;
        document.querySelectorAll('#ProductGridContainer .collection.loading, .product-count.loading, .product-count-vertical.loading').forEach(element => element.classList.remove('loading'));
        document.querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner').forEach(spinner => spinner.classList.add('hidden'));
        FacetFiltersForm.toggleActiveFacets(false);
        document.querySelector('[data-cn-filter-error]')?.remove();
        const message = document.createElement('div');
        message.dataset.cnFilterError = '';
        message.className = 'cn-filter-load-error';
        message.setAttribute('role', 'alert');
        message.textContent = 'Die Auswahl konnte nicht geladen werden. Angezeigt wird noch das vorherige Ergebnis. ';
        const retry = document.createElement('button');
        retry.type = 'button';
        retry.textContent = 'Erneut versuchen';
        retry.addEventListener('click', () => FacetFiltersForm.renderPage(FacetFiltersForm.searchParamsPrev, null, false));
        message.append(retry);
        document.getElementById('ProductGridContainer')?.prepend(message);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const localSearches = new Map(Array.from(document.querySelectorAll('cn-filter-search input')).map(input => [input.id, input.value]));
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = ['text', 'range'].includes(event.target.getAttribute('type'));

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
    document.querySelectorAll('cn-filter-search').forEach(search => {
      const input = search.querySelector('input');
      if (input && localSearches.has(input.id)) input.value = localSearches.get(input.id);
      search.apply?.();
    });
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    const applyLabel = html.querySelector('[data-cn-filter-apply-label]')?.textContent;
    if (applyLabel) document.querySelectorAll('[data-cn-filter-apply-label]').forEach(label => { label.textContent = applyLabel; });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const prices = [...form.querySelectorAll('price-range')];
    if (prices.some(range => range.normalizeInputs?.() === false)) return null;
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    if (searchParams === null) return;
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    if (event.target.closest('[data-cn-local-filter]') || event.target.matches('[data-cn-price-handle]')) return;
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      if (!forms.includes(null)) this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    document.querySelectorAll('facet-filters-form').forEach(form => { form.pendingSubmission = false; });
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.requestSequence = 0;
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.fields = Array.from(this.querySelectorAll('[data-cn-price-field]'));
    this.handles = Array.from(this.querySelectorAll('[data-cn-price-handle]'));
    if (this.fields.length !== 2) return;
    this.ceiling = Number(this.fields[1].dataset.ceiling);
    if (!Number.isFinite(this.ceiling)) return;
    this.fields.forEach(field => {
      field.addEventListener('change', event => { this.normalizeInputs(event.currentTarget); });
      field.addEventListener('input', () => {
        const feedback = this.querySelector('[data-cn-price-feedback]');
        if (feedback && !feedback.classList.contains('cn-price-feedback--error')) feedback.textContent = '';
        if (field.getAttribute('aria-invalid') === 'true') this.validateInputs();
        this.syncHandles();
      });
    });
    this.handles.forEach(handle => {
      handle.addEventListener('input', event => this.onSliderInput(event));
      handle.addEventListener('change', event => {
        this.onSliderInput(event);
        const field = this.fields[handle.dataset.cnPriceHandle === 'min' ? 0 : 1];
        field.dispatchEvent(new Event('input', { bubbles: true }));
      });
    });
    const slider = this.querySelector('.cn-price-slider');
    if (slider && this.handles.length === 2) slider.hidden = false;
    this.normalizeInputs();
  }

  parse(value) {
    const compact = String(value).trim().replace(/[\s’']/g, '');
    if (!compact) return null;
    // The fields are rendered without grouping. Accept German decimal commas
    // and pasted German/English grouping, but never silently parse invalid text.
    let normalized = compact;
    if (/^[1-9]\d{0,2}(?:[.,]\d{3})+$/.test(compact)) {
      normalized = compact.replace(/[.,]/g, '');
    } else if (compact.includes(',')) {
      normalized = compact.lastIndexOf(',') > compact.lastIndexOf('.')
        ? compact.replace(/\./g, '').replace(',', '.')
        : compact.replace(/,/g, '');
    }
    if (!/^\d+(?:\.\d{0,2})?$/.test(normalized)) return null;
    const number = Number(normalized);
    return Number.isFinite(number) ? Math.round(Math.min(this.ceiling, Math.max(0, number)) * 100) / 100 : null;
  }

  normalizeInputs(changed) {
    if (this.fields?.length !== 2) return;
    if (!this.validateInputs()) return false;
    const before = this.fields.map(field => field.value);
    let low = this.parse(this.fields[0].value);
    let high = this.parse(this.fields[1].value);
    if (low !== null && high !== null && low > high) {
      if (changed === this.fields[1]) high = low;
      else low = high;
    }
    this.fields[0].value = low === null ? '' : String(low);
    this.fields[1].value = high === null ? '' : String(high);
    this.fields[0].dataset.max = high ?? this.ceiling;
    this.fields[1].dataset.min = low ?? 0;
    this.syncHandles();
    const feedback = this.querySelector('[data-cn-price-feedback]');
    if (feedback && this.fields.some((field, index) => field.value !== before[index])) {
      feedback.textContent = `Preisbereich angepasst: ${low ?? 0} bis ${high ?? this.ceiling} ${this.querySelector('.cn-price-currency')?.textContent.trim() || ''}.`;
    }
    return true;
  }

  validateInputs() {
    let valid = true;
    this.fields.forEach(field => {
      const invalid = Boolean(field.value.trim()) && this.parse(field.value) === null;
      if (invalid) { field.setAttribute('aria-invalid', 'true'); valid = false; }
      else field.removeAttribute('aria-invalid');
    });
    const feedback = this.querySelector('[data-cn-price-feedback]');
    if (feedback) {
      if (!valid) feedback.textContent = 'Bitte gib einen Preis ab 0 mit höchstens zwei Nachkommastellen ein, zum Beispiel 299,90.';
      else if (feedback.classList.contains('cn-price-feedback--error')) feedback.textContent = '';
      feedback.classList.toggle('cn-price-feedback--error', !valid);
    }
    return valid;
  }

  onSliderInput(event) {
    const handle = event.currentTarget;
    const index = handle.dataset.cnPriceHandle === 'min' ? 0 : 1;
    const low = this.parse(this.fields[0].value) ?? 0;
    const high = this.parse(this.fields[1].value) ?? this.ceiling;
    const value = index === 0 ? Math.min(Number(handle.value), high) : Math.max(Number(handle.value), low);
    this.fields[index].value = String(value);
    this.validateInputs();
    this.syncHandles();
  }

  syncHandles() {
    if (this.handles.length !== 2) return;
    const low = this.parse(this.fields[0].value) ?? 0;
    const high = Math.max(low, this.parse(this.fields[1].value) ?? this.ceiling);
    this.handles[0].value = String(low);
    this.handles[1].value = String(high);
    this.handles[0].setAttribute('aria-valuemax', String(high));
    this.handles[1].setAttribute('aria-valuemin', String(low));
    this.handles.forEach(handle => handle.setAttribute('aria-valuetext', `${handle.value} ${this.querySelector('.cn-price-currency')?.textContent.trim() || ''}`.trim()));
    const slider = this.querySelector('.cn-price-slider');
    slider.style.setProperty('--cn-range-start', `${this.ceiling ? low / this.ceiling * 100 : 0}%`);
    slider.style.setProperty('--cn-range-end', `${this.ceiling ? high / this.ceiling * 100 : 100}%`);
    // At coincident endpoints both keyboard-operable thumbs remain reachable.
    this.handles[0].style.zIndex = low >= this.ceiling ? '3' : '2';
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
